/**
 * Base local de la PWA (IndexedDB vía Dexie).
 *
 * Alcance deliberadamente acotado: el coordinador eligió COLA DE REINTENTO
 * LIGERA, no offline-first completo. Eso significa que aquí vive lo necesario
 * para que el encuestador pueda:
 *
 *  1. Abrir la app y el cuestionario sin señal (paquete del instrumento).
 *  2. Capturar entrevistas y encolarlas hasta recuperar red.
 *  3. Evaluar el GPS contra el polígono de su conglomerado sin servidor.
 *  4. Seguir el recorrido del salto sistemático y registrar disposiciones.
 *
 * Lo que NO hace: resolver conflictos de edición concurrente ni sincronizar en
 * ambos sentidos. El dispositivo es la única fuente de una entrevista hasta que
 * llega al servidor, y el servidor nunca la devuelve modificada.
 */

import Dexie, { type Table } from 'dexie';

/* -------------------------------------------------------------------------- */
/*                                   Tipos                                    */
/* -------------------------------------------------------------------------- */

export type QueueItemKind = 'interview' | 'household';

export type QueueItemStatus =
  /** Esperando envío. */
  | 'pending'
  /** En vuelo. */
  | 'sending'
  /** Aceptada por el servidor; se conserva un rato para que el encuestador la vea. */
  | 'synced'
  /** El servidor la rechazó por una razón que no se resuelve reintentando. */
  | 'rejected';

export interface QueueItem {
  /** clientUuid; es también la llave de idempotencia en el servidor. */
  id: string;
  kind: QueueItemKind;
  payload: unknown;
  status: QueueItemStatus;
  attempts: number;
  lastAttemptAt: number | null;
  lastError: string | null;
  createdAt: number;
  syncedAt: number | null;
  /** Para mostrar algo legible en la pantalla de sincronización. */
  summary: string;
}

/** Entrevista en curso, guardada tras cada respuesta. */
export interface DraftInterview {
  clientUuid: string;
  assignmentId: string;
  clusterId: string;
  expectedFrameSeccion: number | null;
  isPilot: boolean;
  startedAt: string;
  /** Aleatorización que entregó el servidor al abrir. */
  personajeOrder: string[];
  partyOrder: string[];
  experimentCellId: string | null;
  experimentCell: { id: string; personajeId: string; labelText: string } | null;
  i6PersonajeIds: string[];
  trimState: Record<string, boolean>;
  /** Respuestas por clave compuesta. */
  answers: Record<string, DraftAnswer>;
  /** Código de la pregunta en la que quedó la entrevista. */
  cursor: string | null;
  gps: {
    latitude: number;
    longitude: number;
    accuracyMeters: number | null;
    capturedAt: string;
  } | null;
  gpsDetection: unknown | null;
  outOfPolygonReason: string | null;
  outOfPolygonNote: string | null;
  coloniaDeclared: string | null;
  householdClientUuid: string | null;
  /** Datos de contacto de Q1/Q2. Se envían y se borran del dispositivo. */
  contactName: string | null;
  contactPhone: string | null;
  thirdPartyPresent: boolean | null;
  observations: string | null;
  updatedAt: number;
  /** La entrevista terminó y espera envío. */
  finishedAt: string | null;
  /** Disposición con la que se cerró si no fue completa. */
  terminatedAs: string | null;
}

export interface DraftAnswer {
  questionCode: string;
  rowCode?: string | null;
  rankOrder?: number | null;
  optionCode?: string | null;
  numericValue?: number | null;
  textValue?: string | null;
  specifyText?: string | null;
  isPrimary?: boolean | null;
}

/** Vivienda del recorrido, con sus intentos de contacto. */
export interface DraftHousehold {
  clientUuid: string;
  assignmentId: string;
  clusterId: string;
  sequenceNumber: number | null;
  manzanaCvegeo: string | null;
  addressNote: string | null;
  latitude: number | null;
  longitude: number | null;
  accuracyMeters: number | null;
  disposition: string;
  eligibleAdults: number | null;
  visits: {
    attemptNumber: number;
    timeSlot: string;
    visitedAt: string;
    disposition: string;
    note?: string;
  }[];
  createdAt: number;
  updatedAt: number;
  /** Ya se envió al servidor. */
  queued: boolean;
}

/** Caché de datos del servidor que la app necesita sin señal. */
export interface CacheEntry {
  key: string;
  value: unknown;
  fetchedAt: number;
}

/* -------------------------------------------------------------------------- */
/*                                Base Dexie                                  */
/* -------------------------------------------------------------------------- */

/** Un tramo de consumo medido en el dispositivo, pendiente de enviar. */
export interface StoredUsageSample {
  id?: number;
  bytes: number;
  connection: 'movil' | 'wifi' | 'desconocida';
  source: 'asn' | 'manual' | 'desconocido';
  asn: number | null;
  startedAt: string;
  endedAt: string;
}


class CapiDatabase extends Dexie {
  queue!: Table<QueueItem, string>;
  drafts!: Table<DraftInterview, string>;
  households!: Table<DraftHousehold, string>;
  cache!: Table<CacheEntry, string>;
  /**
   * Tramos de consumo medidos y todavía sin enviar.
   *
   * Se persisten antes de intentar el envío: si el encuestador cierra la
   * aplicación o se queda sin señal, el consumo ya medido no se pierde, que
   * es justo el escenario normal en campo.
   */
  usage!: Table<StoredUsageSample, number>;

  constructor() {
    super('capi-tecate-2027');
    this.version(1).stores({
      queue: 'id, kind, status, createdAt',
      drafts: 'clientUuid, assignmentId, updatedAt, finishedAt',
      households: 'clientUuid, assignmentId, clusterId, sequenceNumber, queued',
      cache: 'key, fetchedAt',
    });

    // v2 añade la medición de consumo de datos. Dexie migra solo: las bases
    // existentes en los teléfonos conservan todo lo capturado.
    this.version(2).stores({
      queue: 'id, kind, status, createdAt',
      drafts: 'clientUuid, assignmentId, updatedAt, finishedAt',
      households: 'clientUuid, assignmentId, clusterId, sequenceNumber, queued',
      cache: 'key, fetchedAt',
      usage: '++id, startedAt, connection',
    });
  }
}

export const db = new CapiDatabase();

/* -------------------------------------------------------------------------- */
/*                                   Caché                                    */
/* -------------------------------------------------------------------------- */

export const CACHE_KEYS = {
  fieldPackage: 'field-package',
  assignments: 'assignments',
  dispositions: 'dispositions',
  outOfPolygonReasons: 'out-of-polygon-reasons',
  profile: 'profile',
  /** Geometría por sección del marco: `geo:722`. */
  geoSection: (seccion: number) => `geo:${seccion}`,
  /** Geometría por conglomerado: `geo-cluster:B17`. */
  geoCluster: (clusterId: string) => `geo-cluster:${clusterId}`,
} as const;

export async function putCache(key: string, value: unknown): Promise<void> {
  await db.cache.put({ key, value, fetchedAt: Date.now() });
}

export async function getCache<T>(key: string): Promise<{ value: T; fetchedAt: number } | null> {
  const entry = await db.cache.get(key);
  return entry ? { value: entry.value as T, fetchedAt: entry.fetchedAt } : null;
}

export async function clearCache(): Promise<void> {
  await db.cache.clear();
}

/**
 * Borra todo lo del dispositivo.
 *
 * Se usa al cerrar sesión. Se rechaza si queda algo sin sincronizar: borrar la
 * cola perdería trabajo de campo de forma irrecuperable, y eso no puede ocurrir
 * por un toque accidental en "salir".
 */
export async function wipeDevice(options: { force?: boolean } = {}): Promise<
  { wiped: true } | { wiped: false; pending: number }
> {
  const pending = await db.queue.where('status').anyOf('pending', 'sending', 'rejected').count();
  if (pending > 0 && !options.force) {
    return { wiped: false, pending };
  }
  await Promise.all([
    db.queue.clear(),
    db.drafts.clear(),
    db.households.clear(),
    db.cache.clear(),
  ]);
  return { wiped: true };
}

/** Cuántas entrevistas y viviendas esperan envío. */
export async function pendingCounts(): Promise<{
  interviews: number;
  households: number;
  rejected: number;
  drafts: number;
}> {
  const [interviews, households, rejected, drafts] = await Promise.all([
    db.queue.where({ kind: 'interview' }).filter((i) => i.status === 'pending' || i.status === 'sending').count(),
    db.queue.where({ kind: 'household' }).filter((i) => i.status === 'pending' || i.status === 'sending').count(),
    db.queue.where({ status: 'rejected' }).count(),
    db.drafts.filter((d) => d.finishedAt === null).count(),
  ]);
  return { interviews, households, rejected, drafts };
}
